import { useRef } from 'react';
import styles from './sectionsIndex.module.scss';
import { useInterval } from '../miscUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useForceUpdate, usePrevious } from '../../features/visual-analytics/hooks/misc';
import { sendEvent } from '../../features/visual-analytics/hooks/events';

interface TpSectionIndex {
  sectionId: string;
  sectionName: string;
}

export interface TpSectionsIndexContext {
  sectionedDocId: string;
  sections: TpSectionIndex[];
  activeSectionId: string;
}

const contexts: TpSectionsIndexContext[] = [];

export function getSectionsIndexContext(sectionedDocId: string): TpSectionsIndexContext {
  for (const ctx of contexts) if (ctx.sectionedDocId == sectionedDocId) return ctx;
  const newContext: TpSectionsIndexContext = {
    sectionedDocId,
    sections: [],
    activeSectionId: '',
  };
  contexts.push(newContext);
  return newContext;
}

function addSectionIndex(ctx: TpSectionsIndexContext, sectionId: string, sectionName: string) {
  if (!ctx) return;
  if (ctx.sections.some((section) => section.sectionId == sectionId)) return;
  if (ctx.sections.length == 0) ctx.activeSectionId = sectionId;
  ctx.sections.push({
    sectionId,
    sectionName,
  });
}

interface TpSectionsIndexWrapperProps {
  sectionIndexContext: TpSectionsIndexContext;
  children: React.ReactNode;
}

export const SectionsIndexWrapper = ({ sectionIndexContext, children }: TpSectionsIndexWrapperProps) => {
  const forceUpdate = useForceUpdate();
  const currentContexRef = useRef('');
  const sectionManuallySet = useRef(false);

  const containerRef = useRef(null);

  // In case the content of the sectioned document changes, we want to scroll back to the top. We track this by monitoring changes to DocId
  const prevDocId = usePrevious(sectionIndexContext.sectionedDocId);
  if (prevDocId != sectionIndexContext.sectionedDocId) {
    const docElem = document.getElementById(`scrollContainerSectioned_${prevDocId}`);
    if (docElem) docElem.scrollTo(0, 0);
  }

  useInterval(() => {
    // Because sizes of sections can change dynamically, we monitor everything periodically
    if (!sectionManuallySet.current) {
      const viewportHeight = window.innerHeight;
      // Find the most compatible section
      sectionIndexContext.activeSectionId = '';
      let minSectionPenalty = 1e99;
      for (const section of sectionIndexContext.sections) {
        const sectionElem = document.getElementById(`_section_${section.sectionId}`);
        if (sectionElem) {
          const { top, bottom } = sectionElem.getBoundingClientRect();
          if (bottom > 0 && top < viewportHeight) {
            // a feasible section: some overlap with the viewport
            const sectionPenalty =
              (Math.max(0, -top) + Math.max(0, bottom - viewportHeight)) / Math.max(1, bottom - top);
            if (sectionPenalty < minSectionPenalty) {
              // we retain the section with the least penalty (out of view fraction)
              minSectionPenalty = sectionPenalty;
              sectionIndexContext.activeSectionId = section.sectionId;
            }
          }
        }
      }
    }
    if (JSON.stringify(sectionIndexContext) != currentContexRef.current) {
      currentContexRef.current = JSON.stringify(sectionIndexContext);
      forceUpdate();
    }
  }, 300);

  function navigateToSection(sectionId: string) {
    sendEvent('ExpandCollapsedSection', {
      sectionedDocId: sectionIndexContext.sectionedDocId,
      sectionId,
    });
    sectionManuallySet.current = true;
    setTimeout(() => {
      sectionManuallySet.current = false;
    }, 2000);
    sectionIndexContext.activeSectionId = sectionId;
    forceUpdate();
    setTimeout(() => {
      const sectionElem = document.getElementById(`_section_${sectionId}`);
      if (sectionElem) sectionElem.scrollIntoView({ behavior: 'smooth' });
    }, 1);
  }

  return (
    <div className={styles.container}>
      <div
        className={styles.scrollContainer}
        ref={containerRef}
        id={`scrollContainerSectioned_${sectionIndexContext.sectionedDocId}`}
      >
        {children}
      </div>
      {sectionIndexContext.sections.length >= 2 && (
        <div className={styles.tabsContainer}>
          {sectionIndexContext.sections.map((section) => (
            <div
              className={section.sectionId == sectionIndexContext.activeSectionId ? styles.tabActive : styles.tab}
              key={section.sectionId}
              onClick={() => {
                navigateToSection(section.sectionId);
              }}
            >
              <div
                className={section.sectionId == sectionIndexContext.activeSectionId ? styles.iconActive : styles.icon}
              >
                <FontAwesomeIcon icon={['fas', 'circle']} />
              </div>
              {section.sectionName}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface TpSectionIndexProps {
  sectionIndexContext: TpSectionsIndexContext;
  sectionId: string;
  sectionName: string;
  children: React.ReactNode;
}

export const SectionIndex = ({ sectionIndexContext, sectionId, sectionName, children }: TpSectionIndexProps) => {
  addSectionIndex(sectionIndexContext, sectionId, sectionName);

  return (
    <div id={`_section_${sectionId}`} style={{ scrollMarginTop: '30px' }}>
      {children}
    </div>
  );
};
